/**
 * Design System - Color Tokens
 * Palette di colori standardizzata per l'applicazione
 */

export const colors = {
  // Primary Colors
  primary: {
    50: '#e3f2fd',
    100: '#bbdefb',
    200: '#90caf9',
    300: '#64b5f6',
    400: '#42a5f5',
    500: '#2196f3', // Main
    600: '#1e88e5',
    700: '#1976d2',
    800: '#1565c0',
    900: '#0d47a1',
  },

  // Secondary Colors
  secondary: {
    50: '#fce4ec',
    100: '#f8bbd9',
    200: '#f48fb1',
    300: '#f06292',
    400: '#ec407a',
    500: '#e91e63', // Main
    600: '#d81b60',
    700: '#c2185b',
    800: '#ad1457',
    900: '#880e4f',
  },

  // Success Colors
  success: {
    50: '#e8f5e8',
    100: '#c8e6c9',
    200: '#a5d6a7',
    300: '#81c784',
    400: '#66bb6a',
    500: '#4caf50', // Main
    600: '#43a047',
    700: '#388e3c',
    800: '#2e7d32',
    900: '#1b5e20',
  },

  // Error Colors
  error: {
    50: '#ffebee',
    100: '#ffcdd2',
    200: '#ef9a9a',
    300: '#e57373',
    400: '#ef5350',
    500: '#f44336', // Main
    600: '#e53935',
    700: '#d32f2f',
    800: '#c62828',
    900: '#b71c1c',
  },

  // Warning Colors
  warning: {
    50: '#fff8e1',
    100: '#ffecb3',
    200: '#ffe082',
    300: '#ffd54f',
    400: '#ffca28',
    500: '#ffc107', // Main
    600: '#ffb300',
    700: '#ffa000',
    800: '#ff8f00',
    900: '#ff6f00',
  },

  // Info Colors
  info: {
    50: '#e1f5fe',
    100: '#b3e5fc',
    200: '#81d4fa',
    300: '#4fc3f7',
    400: '#29b6f6',
    500: '#03a9f4', // Main
    600: '#039be5',
    700: '#0288d1',
    800: '#0277bd',
    900: '#01579b',
  },

  // Neutral Colors
  neutral: {
    50: '#fafafa',
    100: '#f5f5f5',
    200: '#eeeeee',
    300: '#e0e0e0',
    400: '#bdbdbd',
    500: '#9e9e9e',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // Semantic Colors
  semantic: {
    background: {
      default: '#ffffff',
      paper: '#fafafa',
      elevated: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
      disabled: '#bdbdbd',
      hint: '#9e9e9e',
    },
    divider: '#e0e0e0',
    border: '#e0e0e0',
    // Status Colors
    status: {
      active: {
        background: '#4caf5015', // success.main con alpha 15 (8% opacity)
        text: '#4caf50', // success.main per il testo
        border: '#4caf50', // success.main per il bordo
      },
      inactive: {
        background: 'transparent',
        text: '#757575', // text.secondary
        border: '#757575',
      },
      confirmed: {
        background: '#4caf5015', // success.main con alpha 15 (8% opacity)
        text: '#4caf50', // success.main per il testo
        border: '#4caf50', // success.main per il bordo
      },
      pending: {
        background: '#ffc10715', // warning.main con alpha 15 (8% opacity)
        text: '#ffc107', // warning.main per il testo
        border: '#ffc107', // warning.main per il bordo
      },
      cancelled: {
        background: '#f4433615', // error.main con alpha 15 (8% opacity)
        text: '#f44336', // error.main per il testo
        border: '#f44336', // error.main per il bordo
      },
      refunded: {
        background: '#2196f315', // info.main con alpha 15 (8% opacity)
        text: '#2196f3', // info.main per il testo
        border: '#2196f3', // info.main per il bordo
      },
      error: {
        background: '#f4433615', // error.main con alpha 15 (8% opacity)
        text: '#f44336', // error.main per il testo
        border: '#f44336', // error.main per il bordo
      },
      marketingConsent: {
        background: '#4caf5015', // success.main con alpha 15 (8% opacity)
        text: '#4caf50', // success.main per il testo
        border: '#4caf50', // success.main per il bordo
      },
      marketingConsentDenied: {
        background: '#f4433615', // error.main con alpha 15 (8% opacity)
        text: '#f44336', // error.main per il testo
        border: '#f44336', // error.main per il bordo
      },
    },
    // Action Colors - Standardizzati per tutto il progetto
    actions: {
      view: {
        color: '#1976d2', // Blu per Visualizza
        hoverBackground: '#e3f2fd', // Blu chiaro
        hoverColor: '#1565c0', // Blu scuro
      },
      edit: {
        color: '#f57c00', // Arancione per Modifica
        hoverBackground: '#fff3e0', // Arancione chiaro
        hoverColor: '#e65100', // Arancione scuro
      },
      delete: {
        color: '#d32f2f', // Rosso per Elimina
        hoverBackground: '#ffebee', // Rosso chiaro
        hoverColor: '#b71c1c', // Rosso scuro
      },
    },
  },
} as const;

// Export per Material-UI theme
export const palette = {
  primary: {
    main: colors.primary[500],
    light: colors.primary[300],
    dark: colors.primary[700],
    contrastText: '#ffffff',
  },
  secondary: {
    main: colors.secondary[500],
    light: colors.secondary[300],
    dark: colors.secondary[700],
    contrastText: '#ffffff',
  },
  success: {
    main: colors.success[500],
    light: colors.success[300],
    dark: colors.success[700],
    contrastText: '#ffffff',
  },
  error: {
    main: colors.error[500],
    light: colors.error[300],
    dark: colors.error[700],
    contrastText: '#ffffff',
  },
  warning: {
    main: colors.warning[500],
    light: colors.warning[300],
    dark: colors.warning[700],
    contrastText: '#000000',
  },
  info: {
    main: colors.info[500],
    light: colors.info[300],
    dark: colors.info[700],
    contrastText: '#ffffff',
  },
  grey: {
    50: colors.neutral[50],
    100: colors.neutral[100],
    200: colors.neutral[200],
    300: colors.neutral[300],
    400: colors.neutral[400],
    500: colors.neutral[500],
    600: colors.neutral[600],
    700: colors.neutral[700],
    800: colors.neutral[800],
    900: colors.neutral[900],
  },
  background: {
    default: colors.semantic.background.default,
    paper: colors.semantic.background.paper,
  },
  text: {
    primary: colors.semantic.text.primary,
    secondary: colors.semantic.text.secondary,
    disabled: colors.semantic.text.disabled,
  },
  divider: colors.semantic.divider,
} as const;
